import type { PaneSlot } from '~/assets/vehiclePlan';
import type { GlassArea } from '~/lib/api';

/**
 * Which physical pane is which API area, given where the driver sits.
 *
 * This is the whole reason the handedness gate exists. `DRIVER_SIDE_FRONT_WINDOW` is a
 * DIFFERENT PHYSICAL WINDOW in a right-hand-drive market than a left-hand-drive one.
 * The diagram is physical (left/right as drawn); the API enum is role-based
 * (driver/passenger). Get the mapping wrong and the evidence is mislabelled silently —
 * the photo is of a real window, the enum names a real area, and nothing downstream
 * can detect that they disagree.
 *
 * So the side panes are inert in the UI until a side has been chosen. Not a default,
 * not a guess: there is no safe default here.
 */
export type DriverSide = 'right' | 'left';

const BY_SIDE: Record<DriverSide, Partial<Record<PaneSlot, GlassArea>>> = {
  // Right-hand drive: the driver sits on the right, so the RIGHT panes are driver-side.
  right: {
    rightFront: 'DRIVER_SIDE_FRONT_WINDOW',
    rightRear: 'DRIVER_SIDE_REAR_WINDOW',
    rightQuarter: 'DRIVER_SIDE_QUARTER_GLASS',
    leftFront: 'PASSENGER_SIDE_FRONT_WINDOW',
    leftRear: 'PASSENGER_SIDE_REAR_WINDOW',
    leftQuarter: 'PASSENGER_SIDE_QUARTER_GLASS',
  },
  left: {
    leftFront: 'DRIVER_SIDE_FRONT_WINDOW',
    leftRear: 'DRIVER_SIDE_REAR_WINDOW',
    leftQuarter: 'DRIVER_SIDE_QUARTER_GLASS',
    rightFront: 'PASSENGER_SIDE_FRONT_WINDOW',
    rightRear: 'PASSENGER_SIDE_REAR_WINDOW',
    rightQuarter: 'PASSENGER_SIDE_QUARTER_GLASS',
  },
};

/** Same window in either market, so these need no side. */
const INDEPENDENT: Partial<Record<PaneSlot, GlassArea>> = {
  windscreen: 'FRONT_WINDSHIELD',
  rearScreen: 'REAR_WINDSHIELD',
  // Not SUNROOF — that value is rejected by the API. The enum is ROOF_GLASS.
  sunroof: 'ROOF_GLASS',
};

/**
 * The API area for a pane, or null when the pane depends on handedness and no side has
 * been chosen yet. A null return is why the pane is not tappable.
 */
/**
 * The same fact in the API's vocabulary, for `claim.handedness`.
 *
 * It lives here rather than at the call site because this file already owns
 * every "given where the driver sits, what does that mean" inversion — and this
 * one inverts a side, which is the class of mistake the whole module exists to
 * prevent. A `side === 'right' ? 'rhd' : 'lhd'` inline in a submit hook is one
 * transposed ternary away from telling the backend the opposite of what the
 * operator chose, with nothing downstream able to detect it.
 */
export function claimHandedness(side: DriverSide): 'rhd' | 'lhd' {
  return side === 'right' ? 'rhd' : 'lhd';
}

export function areaForPane(slot: PaneSlot, side: DriverSide | null): GlassArea | null {
  const independent = INDEPENDENT[slot];
  if (independent) return independent;
  if (side === null) return null;
  return BY_SIDE[side][slot] ?? null;
}

/** Inverse, for rendering an existing selection back onto the diagram. */
export function paneForArea(area: GlassArea, side: DriverSide | null): PaneSlot | null {
  for (const [slot, mapped] of Object.entries(INDEPENDENT)) {
    if (mapped === area) return slot as PaneSlot;
  }
  if (side === null) return null;
  for (const [slot, mapped] of Object.entries(BY_SIDE[side])) {
    if (mapped === area) return slot as PaneSlot;
  }
  return null;
}
